/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { validate } from "../../../../utils/validations";
import { setConfirmDialog, setOpenCustomDailog, setOpenToast } from "../../../../store/helpers/helperReducers";
import { useStoreDispatch } from "../../../../store/hooks";
import {
  useGetProfileQuery,
  useCreateProfileMutation,
  useUpdateProfileMutation,
  useDeleteProfileMutation,
} from "../../../../store/api/admin/adminApiSlice";
import { DIALOGOPENFOR, OP_CODE_GROUPS } from "../../../../globals/globals";
import { IGeoRegion, IProfile } from "../../../../store/api/admin/types";
import { useSelector } from "react-redux";
import { selectedProfilePermissionsSelector, selectedProfileSelector } from "../../../../store/api/admin/selectors";
import { resetProfile } from "../../../helpers";
import { errorMessage, titleCase } from "../../../../utils/helpers";
import { setSelectedProfile } from "../../../../store/api/admin/reducers";
import { AppState } from "../../../../store";
import { confirmDialogSelector } from "../../../../store/helpers/helperSelectors";
import { IList } from "../../../../store/api/tasks/types";
import { useRegionDisplay } from "../../../../pages/Administrations/AccessControls/Regions/hooks";

export const useEditProfile = (profile: IProfile) => {
  const dispatch = useStoreDispatch();
  const { geoRegions } = useRegionDisplay();

  const [updateProfile] = useUpdateProfileMutation();
  const [createProfile] = useCreateProfileMutation();
  const openConfirmDialog = useSelector(confirmDialogSelector);
  const autoCompleteTextRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState("");
  const [editableProfile, setEditableProfile] = useState<IProfile | null>(null);

  const [adminOpCodes, setAdminOpCodes] = useState<string[]>([]);
  const [appOpCodes, setAppOpCodes] = useState<string[]>(["view_profile", "view_topology", "view_sn", "view_label_management"]);
  const [errMsg, setErrMsg] = useState<{ [key: string]: string }>({});

  const [saveButtonEnabled, setSaveButtonEnabled] = useState(false);

  const handleCurrentDomainChange = useCallback((opCodeGroups: string[]) => {
    const adminOps: string[] = [];
    const appOps: string[] = [];
    if (opCodeGroups.length > 0) {
      opCodeGroups.forEach((opCode) => {
        if (OP_CODE_GROUPS[opCode]?.isAdmin) {
          adminOps.push(opCode);
        } else {
          appOps.push(opCode);
        }
      });
    }

    setAdminOpCodes(adminOps);
    setAppOpCodes(appOps);
  }, []);

  // This useEffect only runs once to set the initial state
  useEffect(() => {
    if (!editableProfile && profile && (profile.id || profile.name === "duplicate")) {
      // console.log("Edit Profile Page Initializing...");
      setName(profile.id ? profile.name : "");

      handleCurrentDomainChange(profile.opCodeGroups || []);

      setEditableProfile(profile);
    } else if (!editableProfile && !profile.id) {
      setEditableProfile(resetProfile);
    }
  }, [editableProfile, handleCurrentDomainChange, profile]);

  // This useEffect is basically just for updating the state of the "Save" button
  useEffect(() => {
    const enableButton = (): boolean => {
      if (!editableProfile || !profile) {
        return false;
      }

      if (!name || name.length <= 0) {
        return false;
      }

      if (name !== editableProfile.name) {
        return true;
      }

      if ([...adminOpCodes, ...appOpCodes].length !== (editableProfile.opCodeGroups || []).length) {
        return true;
      }

      // Yes, I realize using "JSON.stringify" to compare two objects is not completely accurate, but it will do

      if (JSON.stringify(profile.opCodeGroups) !== JSON.stringify([...adminOpCodes, ...appOpCodes])) {
        return true;
      }
      if (JSON.stringify(profile.geoRegions) !== JSON.stringify(editableProfile.geoRegions)) {
        return true;
      }

      return false;
    };

    setSaveButtonEnabled(enableButton());
  }, [name, adminOpCodes, appOpCodes, editableProfile, profile]);

  const prepareProfileForUpdate = (): IProfile | null => {
    if (!editableProfile) return null;

    // TODO: Any preliminary validation can occur here

    let updatedProfile = { ...editableProfile, name: name } as IProfile;

    // We need to update the default AdminOpCodes and AppOpCodes
    let newOpCodeGroups = [...adminOpCodes, ...appOpCodes];
    updatedProfile = {
      ...updatedProfile,
      opCodeGroups: newOpCodeGroups,
    };

    return updatedProfile;
  };

  /***
   * Handler functions
   */

  const handleNameChange = (event: any) => {
    setName(event.target.value);
  };

  const handleOnChangeAdminOperation = (event: any) => {
    const { checked, value } = event.target;
    setAdminOpCodes(checked ? [...adminOpCodes, value] : adminOpCodes.filter((op) => op !== value));
  };

  const handleOnChangeAppOperation = (event: any) => {
    const { checked, value } = event.target;
    let values = [value];
    if (checked && OP_CODE_GROUPS[value].child) {
      const addedOpCodeName = OP_CODE_GROUPS[value].child;
      if (addedOpCodeName && OP_CODE_GROUPS[addedOpCodeName]) {
        values = [...values, addedOpCodeName];
      }
    } else if (!checked && OP_CODE_GROUPS[value].parent) {
      const parentOpCode = OP_CODE_GROUPS[value].parent;
      if (parentOpCode && appOpCodes.includes(parentOpCode)) {
        values = [];
      }
    }
    const newAppCodes = checked ? [...appOpCodes, ...values] : appOpCodes.filter((op) => !values.includes(op));

    setAppOpCodes(newAppCodes);
  };

  const handleRegionAutocompleteSelect = (event: any, newValue: IList[]) => {
    event?.stopPropagation();

    if (!editableProfile) return null;

    const newRegions: IGeoRegion[] = [];
    newValue.forEach((val) => {
      if (!newRegions.find((region) => region.name === val.title)) {
        newRegions.push({ name: val.title, objectKey: val.value, siteGroups: [] });
      }
    });
    const updatedProfile = { ...editableProfile, geoRegions: newRegions } as IProfile;

    setEditableProfile(updatedProfile);
    return updatedProfile;
  };

  const handleSave = (handleCancel: () => void) => {
    if (!editableProfile) return;
    if (editableProfile.id && editableProfile.id > 0) {
      dispatch(setConfirmDialog(DIALOGOPENFOR.PROFILE_SAVE));
    } else {
      handleClickSaveAction("save", handleCancel);
    }
  };

  const handleClickSaveAction = async (actionName: string, handleCancel: () => void): Promise<boolean> => {
    if (!actionName || !actionName.match("save") || !editableProfile) return false;

    const updatedProfile = prepareProfileForUpdate();
    if (!updatedProfile) {
      throw new Error("Something went wrong!");
    }
    return await submitHandler(updatedProfile, handleCancel);
  };

  const submitHandler = async (profileData: IProfile, handleCancel: () => void): Promise<boolean> => {
    if (!profileData || profileData.id == null) return false;

    const { errorMessages, isValid } = validate({ profileName: profileData.name});
    setErrMsg({ ...errMsg, ...errorMessages });
    
    const isSystemAdmin = profileData.opCodeGroups?.includes("system_admin");
    
    if (!isSystemAdmin && (!profileData.geoRegions || profileData.geoRegions.length === 0)) {
      dispatch(
        setOpenToast({
          openToast: true,
          toastType: "error",
          message: "Region is required!",
        })
      );
      return false;
    }

    if (isValid) {
      try {
        const result: any = (profileData.id || 0) > 0 ? await updateProfile(profileData) : await createProfile(profileData);

        if (result.error || !result.data) {
          throw result.error.data || "Unknown error";
        }

        dispatch(
          setOpenToast({
            openToast: true,
            toastType: "success",
            message: "Profile has been updated",
          })
        );

        // Always close the sheet after successful save (both for new and existing profiles)
        handleCancel();
        return true;
      } catch (error: any) {
        dispatch(
          setOpenToast({
            openToast: true,
            toastType: "error",
            message: errorMessage(error),
          })
        );
        return false;
      }
    }
    return false;
  };

  return {
    editableProfile,
    geoRegions,
    autoCompleteTextRef,
    name,
    handleNameChange,
    adminOpCodes,
    appOpCodes,
    setAppOpCodes,
    handleOnChangeAdminOperation,
    handleOnChangeAppOperation,
    saveButtonEnabled,
    handleSave,
    openConfirmDialog,
    handleClickSaveAction,
    handleRegionAutocompleteSelect,
  };
};

export const useProfileData = () => {
  const dispatch = useStoreDispatch();
  const { data: profileData, refetch } = useProfileDisplay();

  const protectedProfiles = ["Admin", "Tech", "Viewer"];

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [deleteProfile] = useDeleteProfileMutation();

  const [fallbackId, setFallbackId] = useState("None");

  const selectedProfile = useSelector<AppState, IProfile | null>(selectedProfileSelector);
  const selectedPermissions = useSelector(selectedProfilePermissionsSelector);
  const openConfirmDialog = useSelector(confirmDialogSelector);

  const [isCreateProfile, setIsCreateProfile] = useState(false);
  const [isEditProfile, setIsEditProfile] = useState(false);

  useEffect(() => {
    if (!isCreateProfile && !isEditProfile) {
      refetch();
    }
  }, [isCreateProfile, isEditProfile, refetch]);

  useEffect(() => {
    if (selectedPermissions?.getProfileId() === 0 && selectedPermissions?.getProfileName() === "duplicate") {
      setIsEditProfile(true);
    }
  }, [selectedPermissions]);

  useEffect(() => {
    return () => {
      setIsCreateProfile(false);
      setIsEditProfile(false);
    };
  }, []);

  const handleCancel = () => {
    setIsCreateProfile(false);
    setIsEditProfile(false);
    dispatch(setSelectedProfile(resetProfile));
  };

  const deleteHandler = () => {
    dispatch(setConfirmDialog(DIALOGOPENFOR.PROFILE));
  };

  const handleClickDeleteAction = async (actionName: string) => {
    if (!actionName || !selectedProfile) return;
    try {
      if (actionName.match("delete")) {
        const result: any = await deleteProfile({
          profileId: selectedProfile.id,
          fallbackId: fallbackId !== "None" ? fallbackId : "",
        });

        if (result.error || !result.data) {
          throw result.error.data || "Unknown error";
        }

        setIsEditProfile(false);
        dispatch(setOpenCustomDailog(false));
        dispatch(setSelectedProfile(resetProfile));
        dispatch(
          setOpenToast({
            openToast: true,
            toastType: "success",
            message: `${titleCase(selectedProfile.name)} has been deleted`,
          })
        );
        setFallbackId("None");
        refetch();
      }
    } catch (error: any) {
      dispatch(
        setOpenToast({
          openToast: true,
          toastType: "error",
          message: errorMessage(error),
        })
      );
    }
  };

  const handleOnMoreIconClick = (event: React.MouseEvent<HTMLButtonElement>, profileId: number) => {
    setAnchorEl(event.currentTarget);
    const selectedProfileDetails = profileData.filter((item: IProfile) => item.id === profileId)[0];
    dispatch(setSelectedProfile(selectedProfileDetails));
    setIsCreateProfile(false);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOnMenuItemClick = (action: string, profile?: IProfile) => {
    if (profile) {
      dispatch(setSelectedProfile(profile));
    }
    if (action === "Edit") {
      setIsCreateProfile(false);
      setIsEditProfile(true);
    } else if (action === "Delete") {
      deleteHandler();
    } else if (action === "Duplicate") {
      const current = profile || selectedProfile;
      dispatch(setSelectedProfile({ ...current, id: 0, name: "duplicate" }));
    }

    handleMenuClose();
  };

  const handleClickOnAddProfile = () => {
    setIsCreateProfile(true);
    dispatch(setSelectedProfile(resetProfile));
    dispatch(setOpenCustomDailog(true));
  };

 

  return {
    profileData,
    protectedProfiles,
    selectedProfile,
    anchorEl,
    open,
    isCreateProfile,
    isEditProfile,
    openConfirmDialog,
    handleCancel,
    deleteHandler,
    handleClickDeleteAction,
    handleOnMoreIconClick,
    handleMenuClose,
    handleOnMenuItemClick,
    handleClickOnAddProfile,
    fallbackId,
    setFallbackId,
  };
};

export const useProfileDisplay = () => {
  const { data = [], isLoading, refetch } = useGetProfileQuery();
  const [profileData, setProfileData] = useState<IProfile[]>([]);

  useEffect(() => {
    data.length > 0 && setProfileData(data);
  }, [data]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return {
    data: profileData,
    isLoading,
    refetch,
  };
};
